import {
  hasProjectedWessalAssignee,
  isExactWessalComposerAssignee,
  WESSAL_COMPOSER_ASSIGNEE_REQUIRED,
  WESSAL_COMPOSER_FOREIGN_ASSIGNEE,
} from './wessalComposerOwnership';

type WessalControlMode = 'AI_ELIGIBLE' | 'HUMAN' | 'PAUSED' | 'RESOLVED';

export type WessalConversationControl = {
  readonly mode: WessalControlMode;
  readonly controlVersion: number;
  readonly aiEnabled: boolean;
  readonly humanOwnerId?: string | null;
  readonly updatedAt: string;
};

export type WessalWorkspaceContext = {
  readonly accountId: string | number;
  readonly userId: string | number;
};

type AuthoritativeWessalControl = WessalConversationControl & {
  readonly ownedByRequester: boolean;
};

type ChatwootConversationLike = {
  readonly id?: string | number;
  readonly assignee_id?: unknown;
  readonly meta?: unknown;
  readonly custom_attributes?: Readonly<Record<string, unknown>>;
  readonly additional_attributes?: Readonly<Record<string, unknown>>;
};

type AuthenticatedChatwootAxios = {
  post(
    url: string
  ): Promise<{ readonly status: number; readonly data: unknown }>;
};

const acknowledgedControls = new Map<string, WessalConversationControl>();
const CONTROL_MODES = new Set<WessalControlMode>([
  'AI_ELIGIBLE',
  'HUMAN',
  'PAUSED',
  'RESOLVED',
]);

function attributes(
  conversation: ChatwootConversationLike
): Readonly<Record<string, unknown>> {
  return {
    ...(conversation.additional_attributes ?? {}),
    ...(conversation.custom_attributes ?? {}),
  };
}

export function wessalConversationId(
  conversation: ChatwootConversationLike
): string | null {
  const value = attributes(conversation).wessal_conversation_id;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function readWessalControl(
  conversation: ChatwootConversationLike
): WessalConversationControl | null {
  const conversationId = wessalConversationId(conversation);
  if (!conversationId) return null;
  const acknowledged = acknowledgedControls.get(conversationId);
  if (acknowledged) return acknowledged;
  const value = attributes(conversation).wessal_control;
  return parseProjectedControl(value);
}

export async function fetchCurrentWessalControl(
  conversation: ChatwootConversationLike,
  workspace: WessalWorkspaceContext
): Promise<WessalConversationControl | null> {
  const conversationId = wessalConversationId(conversation);
  if (!conversationId) return null;
  const { current } = await authenticateAndReadCurrent(conversation, workspace);
  const control = publicControl(current);
  acknowledgedControls.set(conversationId, Object.freeze(control));
  return control;
}

async function changeControl(input: {
  readonly conversation: ChatwootConversationLike;
  readonly workspace: WessalWorkspaceContext;
  readonly action: 'takeover' | 'resume-ai';
  readonly reason: string;
}): Promise<WessalConversationControl | null> {
  const conversationId = wessalConversationId(input.conversation);
  if (!conversationId) return null;
  const { assertion, current, context } = await authenticateAndReadCurrent(
    input.conversation,
    input.workspace
  );
  if (
    input.action === 'takeover' &&
    current.mode === 'HUMAN' &&
    current.ownedByRequester
  ) {
    const control = publicControl(current);
    acknowledgedControls.set(conversationId, Object.freeze(control));
    return control;
  }
  if (
    input.action === 'resume-ai' &&
    current.mode === 'AI_ELIGIBLE' &&
    current.aiEnabled
  ) {
    const control = publicControl(current);
    acknowledgedControls.set(conversationId, Object.freeze(control));
    return control;
  }

  const panelCredential = await bootstrapPanelCredential(
    input.conversation,
    context,
    assertion
  );
  const csrf = readCsrfCookie();
  const response = await fetch(
    `/wessal-api/v1/conversations/${encodeURIComponent(conversationId)}/${input.action}`,
    {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'error',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'idempotency-key': crypto.randomUUID(),
        'x-wessal-csrf': csrf,
        'x-wessal-panel-credential': panelCredential,
      },
      body: JSON.stringify({
        expectedControlVersion: current.controlVersion,
        reason: input.reason,
      }),
    }
  );
  if (!response.ok) {
    throw new Error(
      `Wessal control interlock rejected the action (${response.status}).`
    );
  }
  const next = parseMutationControl(await readJson(response));
  if (
    next.controlVersion <= current.controlVersion ||
    (input.action === 'takeover' && next.mode !== 'HUMAN') ||
    (input.action === 'resume-ai' &&
      (next.mode !== 'AI_ELIGIBLE' || !next.aiEnabled))
  ) {
    throw new Error(
      'Wessal returned an invalid control transition; action blocked.'
    );
  }
  acknowledgedControls.set(conversationId, Object.freeze({ ...next }));
  return next;
}

export function takeOverWessalConversation(
  conversation: ChatwootConversationLike,
  workspace: WessalWorkspaceContext,
  reason = 'AGENT_TAKEOVER'
): Promise<WessalConversationControl | null> {
  if (
    wessalConversationId(conversation) &&
    !isExactWessalComposerAssignee(conversation, workspace.userId)
  ) {
    return Promise.reject(
      new Error(
        hasProjectedWessalAssignee(conversation)
          ? WESSAL_COMPOSER_FOREIGN_ASSIGNEE
          : WESSAL_COMPOSER_ASSIGNEE_REQUIRED
      )
    );
  }
  return changeControl({ conversation, workspace, action: 'takeover', reason });
}

export function returnWessalConversationToAi(
  conversation: ChatwootConversationLike,
  workspace: WessalWorkspaceContext,
  reason = 'AGENT_RESUME_AI'
): Promise<WessalConversationControl | null> {
  return changeControl({
    conversation,
    workspace,
    action: 'resume-ai',
    reason,
  });
}

async function authenticateAndReadCurrent(
  conversation: ChatwootConversationLike,
  workspace: WessalWorkspaceContext
): Promise<{
  readonly assertion: string;
  readonly current: AuthoritativeWessalControl;
  readonly context: { readonly accountId: string; readonly userId: string };
}> {
  const conversationId = wessalConversationId(conversation);
  if (!conversationId)
    throw new Error(
      'Wessal conversation identity is unavailable; action blocked.'
    );
  const context = workspaceContext(workspace);
  const chatwootAxios = (
    window as Window & { readonly axios?: AuthenticatedChatwootAxios }
  ).axios;
  if (!chatwootAxios)
    throw new Error('Wessal workspace authentication is unavailable.');
  let assertionBody: unknown;
  try {
    const assertionResponse = await chatwootAxios.post(
      `/api/v1/accounts/${encodeURIComponent(context.accountId)}/wessal_session_assertion`
    );
    if (assertionResponse.status !== 200)
      throw new Error('unexpected assertion response');
    assertionBody = assertionResponse.data;
  } catch {
    throw new Error('Wessal workspace authentication failed.');
  }
  if (
    !hasExactKeys(assertionBody, ['assertion']) ||
    !isCredential(assertionBody.assertion)
  ) {
    throw new Error(
      'Wessal workspace authentication returned an invalid assertion.'
    );
  }
  const assertion = assertionBody.assertion;

  const exchangeResponse = await fetch(
    '/wessal-api/v1/auth/chatwoot/exchange',
    {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'error',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify({ assertion }),
    }
  );
  if (!exchangeResponse.ok) {
    throw new Error(
      `Wessal session exchange failed (${exchangeResponse.status}).`
    );
  }
  const exchangeBody = await readJson(exchangeResponse);
  if (
    !hasExactKeys(exchangeBody, ['status', 'expiresInSeconds']) ||
    exchangeBody.status !== 'authenticated' ||
    !Number.isSafeInteger(exchangeBody.expiresInSeconds) ||
    (exchangeBody.expiresInSeconds as number) < 1
  ) {
    throw new Error('Wessal session exchange returned an invalid response.');
  }

  const controlResponse = await fetch(
    `/wessal-api/v1/conversations/${encodeURIComponent(conversationId)}/control`,
    {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'error',
      headers: { accept: 'application/json' },
    }
  );
  if (!controlResponse.ok) {
    throw new Error(
      `Wessal current control is unavailable (${controlResponse.status}); action blocked.`
    );
  }
  return {
    assertion,
    current: parseAuthoritativeControl(await readJson(controlResponse)),
    context,
  };
}

async function bootstrapPanelCredential(
  conversation: ChatwootConversationLike,
  context: { readonly accountId: string; readonly userId: string },
  assertion: string
): Promise<string> {
  const workspaceConversationId = positiveWorkspaceId(conversation.id);
  if (!workspaceConversationId) {
    throw new Error(
      'Chatwoot conversation identity is unavailable; action blocked.'
    );
  }
  const response = await fetch('/wessal-api/v1/panel/bootstrap', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    redirect: 'error',
    headers: {
      accept: 'application/json',
      authorization: `Bearer ${assertion}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      workspaceAdapterKey: 'chatwoot',
      workspaceAccountId: context.accountId,
      workspaceConversationId,
      workspaceUserId: context.userId,
      contextNonce: crypto.randomUUID(),
    }),
  });
  if (!response.ok) {
    throw new Error(
      `Wessal conversation authorization failed (${response.status}).`
    );
  }
  const credential = response.headers.get('x-wessal-panel-credential');
  if (!isCredential(credential)) {
    throw new Error(
      'Wessal conversation authorization returned an invalid credential.'
    );
  }
  return credential;
}

function parseProjectedControl(
  value: unknown
): WessalConversationControl | null {
  if (!isRecord(value)) return null;
  try {
    return parseControl(value, false);
  } catch {
    return null;
  }
}

function parseMutationControl(value: unknown): WessalConversationControl {
  if (!isRecord(value))
    throw new Error(
      'Wessal returned an invalid control transition; action blocked.'
    );
  const allowed = new Set([
    'mode',
    'controlVersion',
    'aiEnabled',
    'humanOwnerId',
    'updatedAt',
  ]);
  if (Object.keys(value).some(key => !allowed.has(key))) {
    throw new Error(
      'Wessal returned an invalid control transition; action blocked.'
    );
  }
  return parseControl(value, true);
}

function parseControl(
  value: Readonly<Record<string, unknown>>,
  validateOwner: boolean
): WessalConversationControl {
  if (
    typeof value.mode !== 'string' ||
    !CONTROL_MODES.has(value.mode as WessalControlMode) ||
    !Number.isSafeInteger(value.controlVersion) ||
    (value.controlVersion as number) < 1 ||
    typeof value.aiEnabled !== 'boolean' ||
    !isTimestamp(value.updatedAt) ||
    (validateOwner &&
      value.humanOwnerId !== undefined &&
      value.humanOwnerId !== null &&
      !isUuid(value.humanOwnerId))
  ) {
    throw new Error(
      'Wessal returned invalid conversation control; action blocked.'
    );
  }
  return {
    mode: value.mode as WessalControlMode,
    controlVersion: value.controlVersion as number,
    aiEnabled: value.aiEnabled,
    ...(value.humanOwnerId !== undefined
      ? { humanOwnerId: value.humanOwnerId as string | null }
      : {}),
    updatedAt: value.updatedAt,
  };
}

function parseAuthoritativeControl(value: unknown): AuthoritativeWessalControl {
  if (
    !hasExactKeys(value, [
      'mode',
      'controlVersion',
      'aiEnabled',
      'ownedByRequester',
      'updatedAt',
    ]) ||
    typeof value.ownedByRequester !== 'boolean'
  ) {
    throw new Error('Wessal returned invalid current control; action blocked.');
  }
  return {
    ...parseControl(value, false),
    ownedByRequester: value.ownedByRequester,
  };
}

function publicControl(
  control: AuthoritativeWessalControl
): WessalConversationControl {
  return {
    mode: control.mode,
    controlVersion: control.controlVersion,
    aiEnabled: control.aiEnabled,
    updatedAt: control.updatedAt,
  };
}

function workspaceContext(workspace: WessalWorkspaceContext): {
  readonly accountId: string;
  readonly userId: string;
} {
  const accountId = positiveWorkspaceId(workspace.accountId);
  const userId = positiveWorkspaceId(workspace.userId);
  if (!accountId || !userId) {
    throw new Error('Chatwoot agent context is unavailable; action blocked.');
  }
  return { accountId, userId };
}

function positiveWorkspaceId(value: unknown): string | null {
  const candidate = typeof value === 'number' ? String(value) : value;
  return typeof candidate === 'string' && /^[1-9]\d*$/u.test(candidate)
    ? candidate
    : null;
}

function readCsrfCookie(): string {
  if (typeof document === 'undefined') {
    throw new Error('Wessal CSRF protection is unavailable; action blocked.');
  }
  const matches = document.cookie
    .split(';')
    .map(value => value.trim())
    .filter(value => value.startsWith('wessal_csrf='));
  if (matches.length !== 1) {
    throw new Error('Wessal CSRF protection is unavailable; action blocked.');
  }
  let value: string;
  try {
    value = decodeURIComponent(matches[0].slice('wessal_csrf='.length));
  } catch {
    throw new Error('Wessal CSRF protection is invalid; action blocked.');
  }
  if (
    value.length < 16 ||
    value.length > 4096 ||
    !/^[A-Za-z0-9._~-]+$/u.test(value)
  ) {
    throw new Error('Wessal CSRF protection is invalid; action blocked.');
  }
  return value;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    throw new Error('Wessal returned an unreadable response; action blocked.');
  }
}

function hasExactKeys(
  value: unknown,
  expected: readonly string[]
): value is Record<string, unknown> {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value).sort();
  return (
    keys.length === expected.length &&
    keys.every((key, index) => key === [...expected].sort()[index])
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCredential(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length >= 16 &&
    value.length <= 4096 &&
    /^[A-Za-z0-9._~-]+$/u.test(value)
  );
}

function isTimestamp(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length <= 64 &&
    Number.isFinite(Date.parse(value))
  );
}

function isUuid(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu.test(
      value
    )
  );
}
